import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { AdvancedCommand, AdvancedCommandRequest } from "@vp/models";
import { NotificationService } from "@vp/shared/notification-service";
import { isNullOrUndefined } from "@vp/shared/utilities";
import { AdvancedCommandsService } from "@vp/virtual-care/data-access";
import { take } from "rxjs";

interface AdvancedCommandDialogData {
  caseId: string | undefined;
  deviceId: string | undefined;
  zoomCallActive: boolean | undefined;
  advancedCommands: AdvancedCommand[] | undefined;
}

@Component({
  selector: "vp-advanced-commands-dialog",
  templateUrl: "./advanced-commands-dialog.component.html",
  styleUrls: ["./advanced-commands-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AdvancedCommandsDialogComponent {
  private _advancedCommandsService = inject(AdvancedCommandsService);
  private notificationService = inject(NotificationService);
  dialogRef = inject<MatDialogRef<AdvancedCommandsDialogComponent>>(MatDialogRef, {
    optional: true
  });
  private data = inject<AdvancedCommandDialogData | undefined>(MAT_DIALOG_DATA, { optional: true });

  caseId: string | undefined;
  deviceId: string | undefined;
  advancedCommands: AdvancedCommand[] | undefined;

  iconMap: Record<string, string> = {
    "Maximize Shield": "call_made",
    "Minimize Shield": "call_received",
    "Restart Shield": "refresh",
    "Restart Device": "reset_tv",
    "Toggle Microphone": "mic",
    "Toggle Speakers": "speaker_group",
    "Volume Up": "volume_up",
    "Volume Down": "volume_down",
    "Shutdown Device": "developer_board_off",
    "Hide Message Center": "messenger_outline",
    "Show Message Center": "messenger",
    "Launch Eko App": "start",
    "Upload PAC Logs": "upload_file",
    "Switch To Speaker View": "person_outline",
    "Switch To Gallery View": "picture_in_pictures",
    "Reset Message Center Default Font Size": "update"
  };

  ngOnInit(): void {
    if (!isNullOrUndefined(this.dialogRef) && !isNullOrUndefined(this.data)) {
      this.caseId = this.data.caseId;
      this.deviceId = this.data.deviceId;
      this.advancedCommands = this.data.advancedCommands;
    }
  }

  sendAdvancedCommand(command: AdvancedCommand): void {
    const sendCommand: AdvancedCommandRequest = {
      commandName: command.methodName,
      commandValue: command.commandValue,
      caseId: this.caseId !== "(No Assigned Value)" ? this.caseId : undefined,
      deviceUserId: this.deviceId
    };

    this._advancedCommandsService
      .sendDeviceCommand(sendCommand)
      .pipe(take(1))
      .subscribe(() => {
        this.notificationService.successMessage(
          `Advanced Command '${command.displayName}' successfully sent`
        );
      });
  }
}
